<template>
    <div class="ui-error-state" :class="`ui-error-state--${size}`" role="alert">
        <div class="ui-error-state__icon" aria-hidden="true">
            <span class="fas" :class="iconClass"></span>
        </div>

        <div class="ui-error-state__body">
            <p class="ui-error-state__title">{{ title }}</p>
            <p v-if="displayMessage" class="ui-error-state__message">{{ displayMessage }}</p>

            <div v-if="showDetails && detailsText" class="ui-error-state__details">
                <button
                    type="button"
                    class="ui-error-state__details-toggle"
                    :aria-expanded="detailsOpen ? 'true' : 'false'"
                    @click="detailsOpen = !detailsOpen"
                >
                    <span class="fas" :class="detailsOpen ? 'fa-chevron-down' : 'fa-chevron-right'" aria-hidden="true"></span>
                    {{ detailsOpen ? 'Hide details' : 'Show details' }}
                </button>
                <pre v-if="detailsOpen" class="ui-error-state__details-content">{{ detailsText }}</pre>
            </div>
        </div>

        <div v-if="ctaLabel || $slots.actions" class="ui-error-state__actions">
            <slot name="actions">
                <button
                    type="button"
                    class="ui-error-state__cta"
                    @click="$emit('retry')"
                >
                    <span class="fas fa-redo" aria-hidden="true"></span>
                    {{ ctaLabel }}
                </button>
            </slot>
        </div>
    </div>
</template>

<script>
export default {
    name: 'UiErrorState',
    props: {
        error: {
            type: [Error, Object, String],
            default: null,
        },
        title: {
            type: String,
            default: 'Something went wrong',
        },
        message: {
            type: String,
            default: null,
        },
        ctaLabel: {
            type: String,
            default: 'Retry',
        },
        variant: {
            type: String,
            default: 'danger',
            validator: (value) => ['danger', 'warning', 'muted'].includes(value),
        },
        size: {
            type: String,
            default: 'md',
            validator: (value) => ['sm', 'md', 'lg'].includes(value),
        },
        showDetails: {
            type: Boolean,
            default: () => Boolean(import.meta?.env?.DEV),
        },
    },
    emits: ['retry'],
    data() {
        return {
            detailsOpen: false,
        };
    },
    computed: {
        iconClass() {
            const map = {
                danger: 'fa-exclamation-circle',
                warning: 'fa-exclamation-triangle',
                muted: 'fa-info-circle',
            };
            return map[this.variant] || map.danger;
        },
        displayMessage() {
            if (this.message) {
                return this.message;
            }
            if (!this.error) {
                return 'Please retry the action. If the problem persists, contact the platform team.';
            }
            if (typeof this.error === 'string') {
                return this.error;
            }
            const apiMessage = this.error?.response?.data?.message
                || this.error?.response?.data?.error
                || this.error?.message;
            return apiMessage || 'Unexpected error. Please retry.';
        },
        detailsText() {
            if (!this.error) {
                return null;
            }
            if (typeof this.error === 'string') {
                return this.error;
            }
            const status = this.error?.response?.status;
            const url = this.error?.config?.url;
            const method = this.error?.config?.method?.toUpperCase();
            const stack = this.error?.stack;
            const lines = [];
            if (method || url) {
                lines.push(`${method || 'REQUEST'} ${url || ''}`.trim());
            }
            if (status) {
                lines.push(`HTTP ${status}`);
            }
            if (this.error?.response?.data) {
                try {
                    lines.push(JSON.stringify(this.error.response.data, null, 2));
                } catch (_error) {
                    console.error(_error);
                }
            } else if (stack) {
                lines.push(stack);
            }
            return lines.length > 0 ? lines.join('\n') : null;
        },
    },
};
</script>

<style lang="scss" scoped>
.ui-error-state {
    align-items: flex-start;
    background-color: $ds-surface-card;
    border: 1px dashed $ds-border-subtle;
    border-radius: $ds-radius-lg;
    color: $ds-text-primary;
    display: flex;
    font-family: $ds-font-family;
    gap: $ds-space-4;

    &--sm {
        padding: $ds-space-4;
    }

    &--md {
        padding: $ds-space-6;
    }

    &--lg {
        padding: $ds-space-8;
    }

    &__icon {
        align-items: center;
        background-color: $ds-tint-danger-bg;
        border-radius: 50%;
        color: $ds-tint-danger-fg;
        display: flex;
        flex-shrink: 0;
        font-size: 18px;
        height: 40px;
        justify-content: center;
        width: 40px;
    }

    &__body {
        flex: 1;
        min-width: 0;
    }

    &__title {
        color: $ds-text-primary;
        font-size: $ds-font-size-md;
        font-weight: $ds-font-weight-semibold;
        line-height: $ds-line-height-tight;
        margin: 0 0 4px;
    }

    &__message {
        color: $ds-text-secondary;
        font-size: $ds-font-size-sm;
        line-height: $ds-line-height-normal;
        margin: 0;
    }

    &__details {
        margin-top: $ds-space-3;
    }

    &__details-toggle {
        background: transparent;
        border: 0;
        color: $ds-text-muted;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: $ds-space-1;
        font-family: $ds-font-family;
        font-size: $ds-font-size-xs;
        padding: 4px 0;

        &:hover {
            color: $ds-text-primary;
        }
    }

    &__details-content {
        background-color: $ds-surface-muted;
        border: 1px solid $ds-border-subtle;
        border-radius: $ds-radius-sm;
        color: $ds-text-secondary;
        font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
        font-size: 11px;
        line-height: 1.5;
        margin: $ds-space-2 0 0;
        max-height: 240px;
        overflow: auto;
        padding: $ds-space-3;
        overflow-wrap: anywhere;
        white-space: pre-wrap;
    }

    &__actions {
        flex-shrink: 0;
    }

    &__cta {
        align-items: center;
        background-color: $ecart-primary;
        border: 0;
        border-radius: $ds-radius-sm;
        color: $ds-text-inverse;
        cursor: pointer;
        display: inline-flex;
        font-family: $ds-font-family;
        font-size: $ds-font-size-sm;
        font-weight: $ds-font-weight-semibold;
        gap: $ds-space-2;
        height: $ds-button-height;
        padding: 0 $ds-space-4;
        transition: background-color $ds-transition-fast, transform $ds-transition-fast;

        &:hover {
            background-color: $ecart-primary-700;
        }

        &:active {
            transform: scale(0.98);
        }

        &:focus-visible {
            outline: 2px solid $ds-border-focus;
            outline-offset: 2px;
        }
    }
}
</style>
